import { TextInput, Button, Group, Radio, Select, Checkbox } from "@mantine/core";
import { useForm } from "@mantine/form";
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import "./CoursePaymentCard.css";
import { currencyFormat } from "../../../utils/currencyFormat";
import { formattedPrice } from "../../../utils/formatted-price";
import { useAppContext } from "../../../context/AppState/AppContext";
import { calculateDiscountedPrice } from "../../../utils/calculateDiscountedPrice";
import TermsModal from "../../../pages/TermsAndConditionsPage/TermsModal/TermsModal";

/**
 * Calculate the price for UNIQUE_PAYMENT in USD
 * @param price
 * @param discount
 * @param isUSD
 * @param officialUSDValue
 * @returns {*} price in USD
 */
const calculateOnePaymentPriceInUSD = (price, discount, isUSD, officialUSDValue) => {
   const usdPrice = isUSD ? price : price / officialUSDValue;
   return calculateDiscountedPrice(usdPrice, discount);
};
/**
 * Creates a label to describe the subscription plan
 * @param cycles - number of cycles
 * @param amount - amount per cycle
 * @param discount - discount percentage
 * @returns {`${string} cuotas de ${string}`}
 */
const formatSubscriptionLabel = (cycles, amount, discount) =>
   `${cycles} cuotas de ${formattedPrice(calculateDiscountedPrice(amount, discount), true)}`;


const CoursePaymentCard = ({
   loadingButton,
   selectedPaymentMethod,
   onPaymentMethodChange,
   onSubmit,
   discountInfo,
   setDiscountInfo,
   paymentDataName,
   paymentDataImage,
   paymentDataPrice,
   paymentDataId,
   subscriptionPlans,
   isSubscription,
   isGrand = false
}) => {
   const [loading, setLoading] = useState(false);
   const [discountApplied, setDiscountApplied] = useState(false);
   const [discountError, setDiscountError] = useState(null);
   const [termsAccepted, setTermsAccepted] = useState(true);
   const [termsModalOpen, setTermsModalOpen] = useState(false);
   const { width, isUSD, officialUSDValue, paymentData } = useAppContext()
   const isMobile = width < 1000;
   // const [selectedSubscriptionPlan, setSelectedSubscriptionPlan] = useState("UNIQUE_PAYMENT");

   const [selectedSubscriptionPlan, setSelectedSubscriptionPlan] = useState(() => {
      if (isSubscription && subscriptionPlans.length > 0) {
         return subscriptionPlans.filter(item => item.origin?.toLowerCase() === selectedPaymentMethod).subscriptionPlanId;
      }
      return "UNIQUE_PAYMENT";
   });

   useEffect(() => {
      // Si es una suscripción o hay planes disponibles
      if (subscriptionPlans && subscriptionPlans.length > 0) {
         const filteredPlans = subscriptionPlans.filter(
            item => item.origin?.toLowerCase() === selectedPaymentMethod?.toLowerCase()
         );

         if (filteredPlans.length > 0) {
            setSelectedSubscriptionPlan(filteredPlans[0].subscriptionPlanId);
         } else if (!isSubscription) {
            setSelectedSubscriptionPlan("UNIQUE_PAYMENT");
         } else {
            setSelectedSubscriptionPlan("");
         }
      }
   }, [selectedPaymentMethod, subscriptionPlans, isSubscription]);

   // This are the subscription plans for the course
   const paymentPriceInUSD = calculateOnePaymentPriceInUSD(paymentDataPrice, discountInfo.percentage, isUSD, officialUSDValue);
   const subscriptionPlansData = isSubscription
      ? subscriptionPlans.filter(item => item.origin?.toLowerCase() === selectedPaymentMethod).map((plan) => ({
         value: plan.subscriptionPlanId,
         label: `Suscripción mensual de ${formattedPrice(paymentPriceInUSD, true)}`,
      }))
      : [
         {
            value: 'UNIQUE_PAYMENT',
            label: 'Un solo pago de: ' + formattedPrice(paymentPriceInUSD, true),
         },
         ...subscriptionPlans.filter(item => item.origin?.toLowerCase() === selectedPaymentMethod).map((plan) => ({
            value: plan.subscriptionPlanId,
            label: formatSubscriptionLabel(
               plan.total_cycles,
               plan.amount_per_cycle,
               discountInfo.percentage
            ),
         })),
      ];


   const handleTermsCheckbox = () => {
      if (!termsAccepted) {
         setTermsAccepted(true);
      } else {
         setTermsAccepted(false);
      }
   };

   // Handler de aceptación desde el modal
   const handleAcceptTerms = () => {
      setTermsAccepted(true);
      setTermsModalOpen(false);
   };

   const form = useForm({ initialValues: { discountCode: "" } });
   const { values, ...formProps } = form;

   // Recibe el codigo por parametro porque el precargado se aplica en el mismo
   // tick en que se escribe en el form, y ahi `values.discountCode` todavia
   // tiene el valor viejo. Sin argumento sigue leyendo el form, como antes.
   const handleApplyDiscount = async (codigoExplicito) => {
      const code = (codigoExplicito || values.discountCode || "").trim();
      if (!code) return;

      try {
         setLoading(true);

         const response = await axios.post(
            `${process.env.REACT_APP_BACKEND_SERVER}/info/checkDiscountCode`,
            {
               code,
               id: paymentDataId
            }
         );

         const discountInfoData = response.data.data;

         if (discountInfoData && discountInfoData.active) {
            setDiscountInfo(discountInfoData);
            setDiscountApplied(true);
            setDiscountError(null);
         } else {
            setDiscountInfo({
               percentage: 0,
               active: false,
               code: "",
               message: "",
            });
            setDiscountApplied(false);
            setDiscountError("Código de descuento inválido");
         }
      } catch (error) {
         console.error("Error fetching discount data:", error);
         setDiscountError("Código de descuento inválido");
      } finally {
         setLoading(false);
      }
   };

   // Codigo que viene de la beca de caravanas: el detalle del curso lo dejo en
   // el carrito y aca se aplica solo, una sola vez. Igual pasa por
   // checkDiscountCode como cualquier otro: si no sirve para esta unidad, el
   // backend lo rechaza y se ve el error, no se regala nada.
   const codigoPrecargado = paymentData?.codigoPrecargado;
   const yaAplicoPrecargado = useRef(false);

   useEffect(() => {
      if (!codigoPrecargado) return;
      if (!paymentDataId) return;
      if (yaAplicoPrecargado.current) return;

      yaAplicoPrecargado.current = true;
      form.setFieldValue("discountCode", codigoPrecargado);
      handleApplyDiscount(codigoPrecargado);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [codigoPrecargado, paymentDataId]);

   return (
      <div className={`coursePaymentCard-father-div ${isGrand ? "coursePaymentCard-father-div_grand" : ""}`}>
         {paymentDataImage && <div className="coursePaymentCard-image-container">
            <img
               className="coursePaymentCard-image-styling"
               src={paymentDataImage}
               alt="payment data"
            />
         </div>}

         <div className="coursePaymentCard-paymentDetails-container">
            <h4 className="coursePaymentCard-course-title">{paymentDataName}</h4>
            <h3 className="coursePaymentCard-price-value">
               {formattedPrice(currencyFormat(paymentDataPrice), isUSD)}

            </h3>
         </div>

         <div>
            <hr className="coursePaymentCard-divisorHr-styling" />
            <div className="coursePaymentCard-firstHr-div-container">
               <form className="coursePaymentCard-form-styling" {...formProps}>
                  <div className="coursePaymentCard-textInput-div">
                     <TextInput
                        classNames={{ label: "coursePaymentCard-textInput-label", input: "coursePaymentCard-textInput-input" }}
                        label="Código de descuento"
                        placeholder="Código de descuento"
                        {...form.getInputProps("discountCode")}
                     />
                     {discountError && (
                        <p className="coursePaymentCard-error-message">
                           {discountError}
                        </p>
                     )}
                  </div>
                  <div className="coursePaymentCard-firstButton-div">
                     <Button
                        fw="normal"
                        fullWidth
                        color={isGrand ? "var(--color-grand-primary)" : "var(--color-secondary)"}
                        type="button"
                        className="CoursePaymentCard--discount-btn"
                        onClick={() => handleApplyDiscount()}
                        loading={loading}
                        disabled={discountApplied}
                     >
                        {isMobile ? "Aplicar" : "Aplicar descuento"}
                     </Button>
                  </div>
               </form>
            </div>
            <hr className="coursePaymentCard-divisorHr-styling" />
            <div className="coursePaymentCard-divisorHr-subtotal">
               <p className="coursePaymentCard-subtotal-p">Subtotal</p>
               <p className="coursePaymentCard-firstHr-second-p-styling">
                  {discountApplied
                     ? `$ ${currencyFormat(calculateDiscountedPrice(paymentDataPrice, discountInfo.percentage))}`
                     : `$ ${currencyFormat(paymentDataPrice)}`}
               </p>
            </div>
            <hr className="coursePaymentCard-divisorHr-styling" />
            <div className="coursePaymentCard-secondHr-div-container">
               <p className="coursePaymentCard-total-p">Total</p>
               <p className="coursePaymentCard-secondHr-second-p-styling">
                  {discountApplied
                     ? `$ ${currencyFormat(calculateDiscountedPrice(paymentDataPrice, discountInfo.percentage))}`
                     : `$ ${currencyFormat(paymentDataPrice)}`}
               </p>
            </div>
            <hr className="coursePaymentCard-divisorHr-styling" />
         </div>

         <div className="coursePaymentCard-selector-container">
            <Radio.Group
               value={selectedPaymentMethod}
               onChange={(id) => {
                  onPaymentMethodChange(id)
               }}
            >
               <Group mt="xs">
                  {!isSubscription &&
                     <>
                        <Radio
                           fw="normal"
                           value="transfer"
                           label="Transferencia bancaria directa"
                           description="Realiza tu pago directamente en nuestras cuentas bancarias. Por favor, usa el número del pedido como referencia de pago. Tu pedido no se procesará hasta que se haya recibido el importe en nuestra cuenta."
                           classNames={{
                              label: "coursePaymentCard-checkbox-label",
                              description: "coursePaymentCard-checkbox-description",
                           }}
                        />
                     </>
                  }
                  {isSubscription && <Radio
                     fw="normal"
                     value="dlocalgo"
                     label="dLocalGo (Tarjeta de crédito o débito)"
                     description="Pagá en tu moneda local y utilizá los medios de pago preferidos."
                     classNames={{
                        label: "coursePaymentCard-checkbox-label",
                        description: "coursePaymentCard-checkbox-description",
                        root: "coursePaymentCard-thirdCheckbox-root",
                     }}
                  />}
                  <Radio
                     fw="normal"
                     value="paypal"
                     label="PayPal"
                     description="Opte por la comodidad y seguridad al pagar con PayPal. La plataforma líder en transacciones en línea le ofrece una experiencia sin problemas, permitiéndole realizar pagos de manera fácil y segura con solo unos clics. (Los pagos se realizan en USD)"
                     classNames={{
                        label: "coursePaymentCard-checkbox-label",
                        description: "coursePaymentCard-checkbox-description",
                        root: "coursePaymentCard-thirdCheckbox-root",
                     }}
                  />

                  {((
                     (!isSubscription && subscriptionPlansData.length > 1)
                     || (isSubscription && subscriptionPlansData.length > 0))
                     && (selectedPaymentMethod === "paypal" || selectedPaymentMethod === "dlocalgo"))
                     && (
                        <div className="coursePaymentCard-paypal-plans-container">
                           <Select data={subscriptionPlansData}
                              value={selectedSubscriptionPlan}
                              onChange={setSelectedSubscriptionPlan}
                              allowDeselect={false}
                              styles={{ root: { width: "100%" } }} />
                        </div>
                     )}
               </Group>
            </Radio.Group>
         </div>


         <TermsModal
            opened={termsModalOpen}
            onAccept={handleAcceptTerms}
            onClose={() => setTermsModalOpen(false)}
         />

         <div className="coursePaymentCard-terms-container">
            <Checkbox
               checked={termsAccepted}
               onChange={handleTermsCheckbox}
               label={
                  <span>
                     He leído y acepto los{" "}
                     <span
                        style={{ textDecoration: "underline", cursor: "pointer" }}
                        onClick={(e) => { e.preventDefault(); setTermsModalOpen(true); }}
                     >
                        Términos y Condiciones
                     </span>
                  </span>
               }
            />
         </div>

         <div className="coursePaymentCard-button-container">
            <Button
               onClick={() => onSubmit(selectedSubscriptionPlan)}
               fw="normal"
               fullWidth
               disabled={!termsAccepted}
               color={isGrand ? "var(--color-grand-accent)" : "secondaryColor"}
               loading={loadingButton}
            >
               Realizar Pago
            </Button>
         </div>
      </div>
   );
};

export default CoursePaymentCard;